import React from 'react';

import { Typography } from '@mui/material';
import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';

import PlanerLogo from 'assets/images/relifCompanies/big-planer-by-relif.webp';
import BigRelifLogo from 'assets/images/relifCompanies/big-relif-transparent.webp';
import SikerLogo from 'assets/images/relifCompanies/big-siker-by-relif.webp';
import ZellerLogo from 'assets/images/relifCompanies/big-zeller-by-relif.webp';
import LidzLogo from 'assets/images/relifCompanies/logo-lidz.webp';
import RelifBackground from 'assets/styles/relif-background-image.svg';
import { BlackBtn } from 'components/UI/Buttons';
import useIsMobile from 'hooks/useIsMobile';
import Footer from 'pages/LandingPageNew/components/Footer';
import Navigation from 'pages/LandingPageNew/components/Navigation';

import { PersonCard } from './components/PersonCard';
import { ProductCardContent } from './components/ProductCard';

const GradientBackground = styled(Box)(({ theme }) => ({
  backgroundImage: `url(${RelifBackground}), linear-gradient(to bottom, #15183A, #638DB8)`,
  backgroundBlendMode: 'overlay',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  backgroundRepeat: 'no-repeat',
  minHeight: '100vh',
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  paddingTop: theme.spacing(8),
  paddingBottom: theme.spacing(8),
}));

const HorizontalConnector = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '-22px',
  [theme.breakpoints.up('xs')]: {
    left: '10%',
    right: '10%',
  },
  [theme.breakpoints.up('md')]: {
    left: '15%',
    right: '15%',
  },
  height: '3px',
  backgroundColor: 'rgba(255, 255, 255, 0.5)',
  zIndex: 0,
}));

const RelifLanding = () => {
  const isMobile = useIsMobile();
  const products = [
    {
      logo: ZellerLogo,
      alt: 'Zeller',
      title: 'Industria Automotriz',
      subtitle: 'Optimiza los leads de tu automotora',
      description:
        'Usando los servicios de Zeller podrás eficientar el proceso comercial entre tus clientes y vendedores, reduce costos y atiende a tu máximo nivel.',
    },
    {
      logo: PlanerLogo,
      alt: 'Planer',
      title: 'Servicios Financieros',
      subtitle: 'Gestiona las interacciones de tus clientes',
      description:
        'Usando los servicios de Planer podrás gestionar documentos, solicitudes y asesoría financiera adecuada para cada cliente.',
    },
    {
      logo: SikerLogo,
      alt: 'Siker',
      title: 'Industria Seguros',
      subtitle: 'Optimiza la venta y asistencia de seguros',
      description:
        'Usando los servicios de Siker podrás personalizar tus procesos de venta, servicios y post venta, reduciendo costos y atendiendo a todos tus leads.',
    },
  ];
  const personTestimonials = [
    {
      name: 'Carla - Usuario de Planer',
      comment:
        'Me encontré con algo que es útil en la vida diaria y para armar un presupuesto mes a mes, y poder generar un ahorro y mejorar mi calidad de vida.',
      rating: 5,
      brandName: 'Planer',
    },
    {
      name: 'Jorge - Cliente de Zeller',
      comment: 'Me cambio mi proceso de ventas en 180º',
      rating: 5,
      brandName: 'Zeller',
    },
    {
      name: 'Anita - Cliente de Zeller',
      comment: 'Zeller ha sido fundamental para mi negocio.',
      rating: 5,
      brandName: 'Zeller',
    },
    {
      name: 'Jesús - Usuario de Planer',
      comment:
        'Pude bancarizarme y en este instante, gracias a la gestión de Planer, pude optar no solo uno, si no dos créditos con distintas instituciones.',
      rating: 5,
      brandName: 'Planer',
    },
    {
      name: 'Diego - Usuario de Planer',
      comment:
        'Ahora seguiré usando Planer para generar más ahorro y poder llegar a mis metas con el apoyo de esta plataforma.',
      rating: 5,
      brandName: 'Planer',
    },
    {
      name: 'Marcos Pérez',
      comment: '',
      rating: 5,
      brandName: 'Siker',
    },
  ];

  return (
    <Box>
      <Navigation handleOpenFinancialAdvisorAuthModal={() => {}} type="Relif" />
      <GradientBackground>
        <Box px={2} py={18}>
          <Typography
            fontFamily="Jost"
            fontWeight="800"
            fontSize={{ xs: '27px', sm: '45px' }}
            lineHeight={{ xs: '34px', sm: '50px' }}
            color="white"
            textAlign="center"
          >
            Aplicaciones de IA y Open Data
            <br />
            {/* <span style={{ color: '#15183A' }}>para transformar industrias</span> */}
            <span>para transformar industrias</span>
            <br />
            ligadas al financiamiento.
          </Typography>
        </Box>
        <Box position="relative" display="flex" justifyContent="center" alignItems="center" mb={8} px={2}>
          <img
            src={BigRelifLogo}
            alt="Relif"
            style={{
              maxWidth: '300px',
              width: '100%',
              height: 'auto',
              position: 'relative',
              zIndex: 1,
            }}
          />
        </Box>
        <Box
          sx={{
            position: 'relative',
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'center',
            gap: 4,
            px: { xs: 2, md: 8 },
            maxWidth: '1400px',
            mx: 'auto',
            mt: 4,
          }}
        >
          <HorizontalConnector />
          {products.map((product) => (
            <ProductCardContent key={product.alt} {...product} />
          ))}
        </Box>
      </GradientBackground>
      <Box
        sx={{
          backgroundColor: 'white',
          py: 8,
          px: { xs: 2, md: 8 },
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: 'flex-start',
          gap: { xs: 2, md: 8 },
          maxWidth: '1400px',
          mx: 'auto',
        }}
      >
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
            width: '100%',
          }}
        >
          <Box
            component="img"
            src={LidzLogo}
            alt="Lidz"
            sx={{
              width: isMobile ? '300px' : '100px',
              height: 'auto',
              borderRadius: 2,
              display: 'block',
              margin: '0 auto',
            }}
          />
        </Box>
        <Box sx={{ flex: 1.5 }}>
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="h4"
              sx={{
                fontFamily: 'Jost',
                fontWeight: 'bold',
                color: '#333',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              ¿ Te interesa la aplicación de IA en la{' '}
              <Box
                component="span"
                sx={{
                  borderBottom: '3px solid #FFD700',
                  display: 'inline-block',
                }}
              >
                industria inmobiliaria
              </Box>
              ?
            </Typography>
          </Box>
          <Typography variant="body1" sx={{ color: '#666', mb: 2, fontWeight: 'bold' }}>
            Te presentamos a nuestros partners inmobiliarios
          </Typography>
          <Typography variant="body1" sx={{ color: '#666', mb: 2, textAlign: 'justify' }}>
            Lidz es nuestro partner experto en la industria, quien logra captar las necesidades de cada uno de sus
            clientes, personalizando aquellos procesos fundamentales dentro de una inmobiliaria. Se integran en cada
            etapa del recorrido de sus clientes, desde el primer contacto hasta postventa, colaborando en diseñar una
            experiencia de venta consultiva personalizada.
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <BlackBtn href="https://www.lidz.ai">Visitar Lidz</BlackBtn>
          </Box>
        </Box>
      </Box>
      <Box
        sx={{
          backgroundColor: '#F5F5F5',
          py: 8,
          px: { xs: 2, md: 8 },
        }}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(3, 1fr)',
            },
            gap: 3,
            maxWidth: '1400px',
            mx: 'auto',
          }}
        >
          {personTestimonials.map((testimonial) => (
            <PersonCard key={testimonial.name} {...testimonial} />
          ))}
        </Box>
      </Box>
      <Footer />
    </Box>
  );
};

export default RelifLanding;
